import React, { 
  Suspense, 
  useEffect, 
  useRef, 
  useState,
  useCallback 
} from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import PrivateRoute from "./routes/privateRoute";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./theme/theme";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import { Box, CircularProgress, Typography } from "@mui/material";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { get } from "./services/apiService";
import getRoutes from "./routes/routeConfig";
import Template from "./pages/cpanel/template";
import SectionPreviewPage from "./pages/cpanel/pages/component-preview/SectionPreviewPage";

function App() {
  const { currentLang } = useAuth();
  const direction = currentLang === "en" ? "ltr" : "rtl";
  const [isOnBoardingComplete, setOnBoardingComplete] = useState(false);
  const [customerRoutes, setCustomerRoutes] = useState([]);
  const [navLabels, setNavLabels] = useState([]);
  const [navLinks, setNavLinks] = useState([]);
  const userId = sessionStorage.getItem("userId");
  const [customContentLoading, setCustomerLoading] = useState(false);
  const location = useLocation();
  const [isSectionLoaded, setIsSectionLoaded] = useState(false);
  const isPageReloadedRef = useRef(false);
  const [tenantId, setTenantId] = useState("");

  // Callback functions
  const onFetchUserStep = useCallback(() => {}, []);

  const onLoading = useCallback((isLoading) => {
    // setCustomerLoading(isLoading)
  }, []);

  const fetchCustomerContentRoutes = useCallback(async () => {
    try {
      const response = await get(`users/${userId}/section/list`, {}, onLoading);
      const customerNavLabels = response?.data?.map((section) => {
        const match = section.translation?.find(
          (t) => t.languageCode === currentLang
        );
        return match?.languageText || section.sectionName;
      })

      if(response.data.length > 0) {
        const tenantId = response?.data[0]?.tenantId;
        if(tenantId){
          setTenantId(tenantId);
        }
      }

      setNavLabels(customerNavLabels);

      const customerNavLinks = response?.data?.map((section) => {
        const match = section.translation?.find(
          (t) => t.languageCode === currentLang
        );
        return  {
          label: match?.languageText || section.sectionName,
          link: `/sections/${section.sectionName.toLowerCase().replace(/\s+/g, "-")}/${section.id}`,
        }
      });

      setNavLinks(customerNavLinks);

      const sections = response?.data?.map((section) => ({
        title: section.sectionName,
        path: `/sections/${section.sectionName.toLowerCase().replace(/\s+/g, "-")}/:id`,
        private: true,
        layout: Template,
        component: SectionPreviewPage,
      }));
      setCustomerRoutes(sections);
    } catch (error) {
      console.error("Error fetching customer content:", error);
    }
  }, [userId, currentLang, onLoading]);

  const fetchUserStep = useCallback(async () => {
    if (userId) {
      const response = await get(`users/${userId}`, {}, onFetchUserStep);
      const currentStep = response?.data?.step;
 
      if (currentStep === "complete") {
        setOnBoardingComplete(true);
        return true;
      }
    }
    return false;
  }, [userId, onFetchUserStep]);

  const handleUser = useCallback(async () => {
    if (userId) {
      setCustomerLoading(true);
      const isComplete = await fetchUserStep();
      if (isComplete && customerRoutes.length === 0) {
        console.log('need to fetch content routes condition true');
        await fetchCustomerContentRoutes();
      }
      setCustomerLoading(false);
    }
  }, [userId, fetchUserStep, customerRoutes.length, fetchCustomerContentRoutes]);

  const fetchCurrentStep = useCallback(async () => {
    if (userId) {
      if (isPageReloadedRef.current) {
        setIsSectionLoaded(true);
      }

      const response = await get(`users/${userId}`, {}, onFetchUserStep);
      const currentStep = response?.data?.step;

      if (currentStep === "complete") {
        setOnBoardingComplete(true);
        fetchCustomerContentRoutes();
      } else {
        setOnBoardingComplete(false);
      }

      if (isPageReloadedRef.current) {
        setIsSectionLoaded(false);
        isPageReloadedRef.current = false;
      }
    }
  }, [userId, onFetchUserStep, fetchCustomerContentRoutes]);

  // Effects
  useEffect(() => {
    const navigationEntries = performance.getEntriesByType('navigation');
    const isReloaded = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
    isPageReloadedRef.current = isReloaded;
  }, []);

  useEffect(() => {
    if (location.pathname === "/cpanel") {
      handleUser();
    }
  }, [userId, location.pathname, handleUser]);

  useEffect(() => {
    if (location.pathname.includes("/sections")) {
      fetchCurrentStep();
    }
  }, [userId, location.pathname, fetchCurrentStep]);

  useEffect(() => {
    if (
      userId &&
      (location?.pathname?.includes("cpanel") ||
       location?.pathname?.includes("sections") ||
       location?.pathname?.includes("customer"))
    ) {
      setNavLinks([]);
      fetchCustomerContentRoutes();
    }
  }, [userId, currentLang, location?.pathname, fetchCustomerContentRoutes]);

  return (
    <div dir={direction}>
      { isSectionLoaded ? (
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Loading.............</Typography>
        </Box>
      ) : customContentLoading === true ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Suspense
          fallback={
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <Routes>
            {getRoutes(isOnBoardingComplete, customerRoutes).map(
              (route, index) => {
                const Component = route.component;
                const Layout = route.layout;

                if (route.private && route.layout) {
                  return (
                    <Route
                      key={index}
                      path={route.path}
                      element={
                        <PrivateRoute>
                          <Layout navLinks={navLinks}>
                            <Component routes={navLabels} tenantId={tenantId}/>
                          </Layout>
                        </PrivateRoute>
                      }
                    />
                  );
                } else if (!route.private && route.layout) {
                  return (
                    <Route
                      key={index}
                      path={route.path}
                      element={
                        <Layout>
                          <Component />
                        </Layout>
                      }
                    />
                  );
                } else {
                  return (
                    <Route
                      key={index}
                      path={route.path}
                      element={<Component />}
                    />
                  );
                }
              }
            )}
          </Routes>
        </Suspense>
      )}
    </div>
  );
}

const Root = () => (
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <CssBaseline />
        <Router>
          <App />
        </Router>
        <ToastContainer
          position="top-right"
          autoClose={6000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </AuthProvider>
    </ThemeProvider>
  </Provider>
);

export default Root;